import { memo, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PostCallWithErrorResponse } from "../Api/ApiServices";
import { ApiConfig } from "../Api/ApiConfig";
import { toastMsg } from "./toast";
import { AppContext } from "../context/AppContext";

const MultipleSelectCheckmarks = ({
  options = [],
  seletedRecord,
  code,
  getList,
  setSelectsRec,
  getTags,
}) => {
  const { adminData } = useContext(AppContext);

  const [isOpen, setIsOpen] = useState(false);
  const [draftTags, setDraftTags] = useState([]);
  // "none" | "partial" | "full"  — keyed by tag name, set ONCE when dropdown opens
  const [tagStates, setTagStates] = useState({});

  const [newTagInput, setNewTagInput] = useState("");
  const initialDraftRef = useRef([]);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inToggle = dropdownRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inToggle && !inMenu) handleClose();
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);

    // setSelectsRec([]);
    setDraftTags([]);
    setTagStates({});
    setNewTagInput("");
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      if (toggleRef.current) {
        const rect = toggleRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 4,
          right: '2.2%',
          width: rect.width,
        });
      }

      // Compute each tag's state from seletedRecord ONCE on open — never read again
      const count = {};
      seletedRecord?.forEach((record) => {
        record?.tags?.forEach((tag) => {
          count[tag.status] = (count[tag.status] || 0) + 1;
        });
      });
      const total = seletedRecord?.length ?? 0;
      const states = {};
      const initialDraft = [];
      const initialPartial = [];
      options.forEach(({ name }) => {
        const c = count[name] ?? 0;
        if (c === 0) states[name] = "none";
        else if (c === total) {
          states[name] = "full";
          initialDraft.push(name);
        } else {
          states[name] = "partial";
          initialPartial.push(name);
        }
      });

      setTagStates(states);
      setDraftTags(initialDraft);
      initialDraftRef.current = { full: initialDraft, partial: initialPartial };
      setNewTagInput("");
      setIsOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCheckboxChange = (tagName) => {
    if (!seletedRecord?.length) {
      toastMsg("error", "Please Select Candidates!");
      return;
    }

    setTagStates((prevStates) => {
      const current = prevStates[tagName];
      // partial → full | full → none | none → full
      const next =
        current === "partial" ? "full" : current === "full" ? "none" : "full";

      setDraftTags((prev) =>
        next === "full"
          ? [...prev.filter((t) => t !== tagName), tagName]
          : prev.filter((t) => t !== tagName)
      );

      return { ...prevStates, [tagName]: next };
    });
  };

  const handleClearAll = () => {
    setDraftTags([]);
    setTagStates((prev) => {
      const cleared = {};
      Object.keys(prev).forEach((k) => (cleared[k] = "none"));
      return cleared;
    });
  };

  const handleReset = () => {
    // Recompute from seletedRecord back to original open state
    const count = {};
    seletedRecord?.forEach((record) => {
      record?.tags?.forEach((tag) => {
        count[tag.status] = (count[tag.status] || 0) + 1;
      });
    });
    const total = seletedRecord?.length ?? 0;
    const states = {};
    const initialDraft = [];
    options.forEach(({ name }) => {
      const c = count[name] ?? 0;
      if (c === 0) states[name] = "none";
      else if (c === total) {
        states[name] = "full";
        initialDraft.push(name);
      } else states[name] = "partial";
    });
    setTagStates(states);
    setDraftTags(initialDraft);
  };

  const handleApply = async () => {
    const { full: initialFull = [], partial: initialPartial = [] } =
      initialDraftRef.current || {};

    const added = draftTags.filter((t) => !initialFull.includes(t));
    const removedFull = initialFull.filter((t) => !draftTags.includes(t));
    const removedPartial = initialPartial.filter(
      (t) => tagStates[t] === "none"
    );
    const removed = [...removedFull, ...removedPartial];

    const calls = [
      ...added.map((t) => ({ tag: t, isAdd: true })),
      ...removed.map((t) => ({ tag: t, isAdd: false })),
    ];

    await Promise.all(
      calls.map(({ tag, isAdd }) =>
        PostCallWithErrorResponse(ApiConfig.UPDATE_TAGS, {
          code,
          tag,
          isAdd,
          userIds: seletedRecord?.map((i) => i?.userId),
          admin: adminData?.email,
        })
      )
    );
    getList(code);
    handleClose();
  };

  const handleAddTag = () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) {
      toastMsg("error", "Please Enter Tag Name!");
      return;
    }
    if (
      options?.some((t) => t?.name?.toLowerCase() === trimmed.toLowerCase())
    ) {
      toastMsg("error", "Tag Name Already Exist!");
      return;
    }
    PostCallWithErrorResponse(ApiConfig.ADD_CUSTOM_TAG, {
      code,
      tagName: trimmed,
    }).then((res) => {
      if (res?.json?.response?.status) {
        getTags();
        toastMsg("success", res?.json?.response?.statusMessage);
        setNewTagInput("");
        setDraftTags((prev) => [...prev, trimmed]);
        setTagStates((prev) => ({ ...prev, [trimmed]: "full" }));
      } else {
        toastMsg("error", res?.json?.response?.statusMessage);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const { full: initialFull = [], partial: initialPartial = [] } =
    initialDraftRef.current || {};
  const hasChanges =
    draftTags.length !== initialFull.length ||
    !draftTags.every((t) => initialFull.includes(t)) ||
    initialPartial.some((t) => tagStates[t] !== "partial");

  const toggleLabel =
    draftTags.length > 0
      ? draftTags.slice(0, 2).join(", ") +
        (draftTags.length > 2 ? ` +${draftTags.length - 2}` : "")
      : "Set Tags";

  return (
    <div
      ref={dropdownRef}
      className="tag-selector-wrap position-relative d-inline-block text-start w-100"
   style={{width:'100%'}}
   >
      <button
        type="button"
        ref={toggleRef}
        onClick={toggleDropdown}
        className="btn-tag-toggle btn d-flex align-items-center justify-content-between w-100 px-2"
        style={{
          backgroundColor: "#fff",
          border: "1px solid var(--ui-border)",
          minHeight: "32px",
          paddingTop: "4px",
          paddingBottom: "4px",
        }}
      >
        <span
          className="text-base fw-medium"
          style={{
            color:
              draftTags.length 
                ? "var(--ui-text-dark)"
                : "var(--ui-text-muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {toggleLabel}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          fill="currentColor"
          style={{ color: "var(--ui-text-muted)", flexShrink: 0 }}
          viewBox="0 0 16 16"
        >
          {isOpen ? (
            <path d="M7.247 4.86l-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
          ) : (
            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
          )}
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="tag-dropdown-menu bg-white d-flex flex-column overflow-hidden"
            style={{
              position: "fixed",
              top: dropdownPos.top,
              right: dropdownPos.right,
              minWidth: dropdownPos.width,
              width: "max-content",
              zIndex: 99999,
              border: "1px solid var(--ui-border)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <div className="tag-list p-1">
              {options.map((tag, index) => {
                const tagName = tag?.name;
                const state = tagStates[tagName] ?? "none";
                const isChecked = state === "full";
                const isIndeterminate = state === "partial";

                return (
                  <div
                    key={tagName}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <label
                      className="tag-label-item form-label flex-grow-1 m-0 d-flex align-items-center px-2"
                      style={{
                        cursor: "pointer",
                        borderRadius: "4px",
                        minHeight: "16px",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCheckboxChange(tagName);
                      }}
                    >
                      <input
                        type="checkbox"
                        className="tag-checkbox form-check-input me-2 m-0"
                        style={{
                          width: "13px",
                          height: "13px",
                          cursor: "pointer",
                          borderColor: "var(--ui-border)",
                          pointerEvents: "none",
                        }}
                        ref={(el) => {
                          if (el) el.indeterminate = isIndeterminate;
                        }}
                        checked={isChecked}
                        onChange={() => {}}
                      />
                      <span
                        className="tag-text text-base"
                        style={{
                          color: "var(--ui-text-dark)",
                          lineHeight: "1",
                        }}
                      >
                        {tagName}
                      </span>
                    </label>
                    {index === 0 && (
                      <button
                        type="button"
                        className="text-action-link ms-4 me-2 flex-shrink-0"
                        style={{
                          fontSize: "0.75rem",
                          lineHeight: "1",
                          background: "transparent",
                          border: "none",
                          color: "var(--brand-primary)",
                          padding: 0,
                          textDecoration: "underline",
                        }}
                        onClick={handleClearAll}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="tag-add-section px-2 py-2"
              style={{ borderTop: "1px solid var(--ui-border)" }}
            >
              <div className="position-relative d-flex align-items-center">
                <input
                  type="text"
                  className="tag-add-input form-control text-base"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add Custom tag"
                  style={{
                    fontSize: "var(--font-size-base)",
                    paddingRight: "30px",
                    minHeight: "28px",
                    paddingTop: "2px",
                    paddingBottom: "2px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="tag-add-btn btn btn-link position-absolute end-0 top-0 bottom-0 text-decoration-none fw-bold px-2 py-0 border-0 d-flex align-items-center justify-content-center"
                  style={{
                    fontSize: "1.2rem",
                    color: "var(--ui-text-muted)",
                    minHeight: "auto",
                  }}
                  aria-label="Add custom tag"
                >
                  +
                </button>
              </div>
            </div>

            <div
              className="tag-footer d-flex align-items-center justify-content-between px-2 py-2"
              style={{
                borderTop: "1px solid var(--ui-border)",
                backgroundColor: "var(--ui-gray-bg)",
              }}
            >
              <button
                type="button"
                onClick={handleReset}
                className="btn-tag-reset btn"
                style={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                  padding: "2px 12px",
                  minHeight: "26px",
                  backgroundColor: "#fff",
                  border: "1px solid var(--ui-border)",
                  color: "var(--ui-text-dark)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={
                  !hasChanges ||
                  Object.values(tagStates).every((s) => s === "none")
                }
                className="btn-tag-apply btn btn-primary ms-3"
                style={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                  padding: "2px 12px",
                  minHeight: "26px",
                }}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default memo(MultipleSelectCheckmarks);
